/**
 * CyberSells Quest System Module
 * Handles daily, weekly, and permanent quests with progress tracking
 */

(function() {
  'use strict';

  const QUEST_STORAGE_KEY = 'cybersells_quests';

  /**
   * Quest definitions
   */
  const QUEST_DEFINITIONS = {
    // DAILY QUESTS (reset at midnight)
    daily: [
      {
        id: 'daily_browse',
        type: 'daily',
        title: 'Product Explorer',
        description: 'View 5 different products',
        icon: '??',
        maxProgress: 5,
        eventType: 'product_view',
        reward: { xp: 50 }
      },
      {
        id: 'daily_cart',
        type: 'daily',
        title: 'Shopping Cart',
        description: 'Add 3 items to cart',
        icon: '??',
        maxProgress: 3,
        eventType: 'cart_add',
        reward: { xp: 75, stats: { luk: 1 } }
      },
      {
        id: 'daily_wishlist',
        type: 'daily',
        title: 'Window Shopping',
        description: 'Add items to wishlist',
        icon: '??',
        maxProgress: 2,
        eventType: 'wishlist_add',
        reward: { xp: 40 }
      }
    ],

    // WEEKLY QUESTS (reset every Monday)
    weekly: [
      {
        id: 'weekly_purchase',
        type: 'weekly',
        title: 'Weekly Shopper',
        description: 'Complete 2 purchases this week',
        icon: '??',
        maxProgress: 2,
        eventType: 'purchase',
        reward: { xp: 300, stats: { str: 2, agi: 1 } }
      },
      {
        id: 'weekly_value',
        type: 'weekly',
        title: 'Big Spender',
        description: 'Spend $100 this week',
        icon: '??',
        maxProgress: 10000, // $100 in cents
        eventType: 'purchase_value',
        reward: { xp: 500, stats: { charisma: 3 } }
      },
      {
        id: 'weekly_explorer',
        type: 'weekly',
        title: 'Collection Explorer',
        description: 'Visit 10 different product collections',
        icon: '???',
        maxProgress: 10,
        eventType: 'collection_view',
        reward: { xp: 200, stats: { perception: 2 } }
      }
    ],

    // PERMANENT QUESTS (one-time completions)
    permanent: [
      {
        id: 'perm_first_purchase',
        type: 'permanent',
        title: 'First Purchase',
        description: 'Complete your first purchase',
        icon: '??',
        maxProgress: 1,
        eventType: 'purchase',
        reward: { xp: 100, stats: { str: 1, int: 1 } }
      },
      {
        id: 'perm_level_5',
        type: 'permanent',
        title: 'Level 5 Reached',
        description: 'Reach player level 5',
        icon: '?',
        maxProgress: 5,
        eventType: 'level_up',
        reward: { xp: 250, stats: { vit: 3 } }
      },
      {
        id: 'perm_level_10',
        type: 'permanent',
        title: 'Level 10 Master',
        description: 'Reach player level 10',
        icon: '??',
        maxProgress: 10,
        eventType: 'level_up',
        reward: { xp: 500, stats: { damage: 5, defense: 3 } }
      },
      {
        id: 'perm_achievement_collector',
        type: 'permanent',
        title: 'Achievement Hunter',
        description: 'Unlock 5 achievements',
        icon: '???',
        maxProgress: 5,
        eventType: 'achievement',
        reward: { xp: 400, stats: { luk: 5 } }
      },
      {
        id: 'perm_big_spender',
        type: 'permanent',
        title: 'VIP Shopper',
        description: 'Spend $500 total',
        icon: '??',
        maxProgress: 50000, // $500 in cents
        eventType: 'total_spent',
        reward: { xp: 1000, stats: { charisma: 5, luk: 3 } }
      }
    ]
  };

  /**
   * Get all quest definitions
   */
  function getAllQuestDefinitions() {
    return [
      ...QUEST_DEFINITIONS.daily,
      ...QUEST_DEFINITIONS.weekly,
      ...QUEST_DEFINITIONS.permanent
    ];
  }

  /**
   * Load quest progress from storage
   */
  function loadQuestProgress() {
    try {
      const stored = localStorage.getItem(QUEST_STORAGE_KEY);
      if (!stored) return {};

      const progress = JSON.parse(stored);
      
      // Check if daily quests need reset
      const lastReset = progress._lastDailyReset || 0;
      const today = new Date().setHours(0, 0, 0, 0);
      if (lastReset < today) {
        resetDailyQuests(progress);
        progress._lastDailyReset = today;
      }

      // Check if weekly quests need reset
      const lastWeeklyReset = progress._lastWeeklyReset || 0;
      const thisWeek = getWeekStart();
      if (lastWeeklyReset < thisWeek) {
        resetWeeklyQuests(progress);
        progress._lastWeeklyReset = thisWeek;
      }

      return progress;
    } catch (error) {
      if (window.cyberSellsConfig?.debug) {
        console.error('[CyberSells] Error loading quest progress:', error);
      }
      return {};
    }
  }

  /**
   * Save quest progress to storage
   */
  function saveQuestProgress(progress) {
    try {
      localStorage.setItem(QUEST_STORAGE_KEY, JSON.stringify(progress));
      return true;
    } catch (error) {
      if (window.cyberSellsConfig?.debug) {
        console.error('[CyberSells] Error saving quest progress:', error);
      }
      return false;
    }
  }

  /**
   * Get start of current week (Monday)
   */
  function getWeekStart() {
    const now = new Date();
    const day = now.getDay();
    const diff = now.getDate() - day + (day === 0 ? -6 : 1); // Adjust for Sunday
    return new Date(now.setDate(diff)).setHours(0, 0, 0, 0);
  }

  /**
   * Reset daily quest progress
   */
  function resetDailyQuests(progress) {
    QUEST_DEFINITIONS.daily.forEach(quest => {
      if (progress[quest.id]) {
        progress[quest.id].currentProgress = 0;
        progress[quest.id].completed = false;
        progress[quest.id].claimedReward = false;
      }
    });
  }

  /**
   * Reset weekly quest progress
   */
  function resetWeeklyQuests(progress) {
    QUEST_DEFINITIONS.weekly.forEach(quest => {
      if (progress[quest.id]) {
        progress[quest.id].currentProgress = 0;
        progress[quest.id].completed = false;
        progress[quest.id].claimedReward = false;
      }
    });
  }

  /**
   * Get all active quests with current progress
   */
  function getActiveQuests(playerData) {
    const progress = loadQuestProgress();
    const allQuests = getAllQuestDefinitions();

    return allQuests.map(quest => {
      const questProgress = progress[quest.id] || {
        currentProgress: 0,
        completed: false,
        claimedReward: false
      };

      // Calculate progress for special quest types
      let currentProgress = questProgress.currentProgress;

      if (quest.eventType === 'level_up') {
        currentProgress = playerData.level;
      } else if (quest.eventType === 'achievement') {
        currentProgress = playerData.achievementCount || 0;
      } else if (quest.eventType === 'total_spent') {
        currentProgress = playerData.totalSpent || 0;
      }

      const completed = currentProgress >= quest.maxProgress;
      const percentage = Math.min(100, (currentProgress / quest.maxProgress) * 100);

      return {
        ...quest,
        currentProgress,
        completed,
        claimedReward: questProgress.claimedReward,
        percentage
      };
    });
  }

  /**
   * Track a quest event and update progress
   */
  function trackQuestEvent(eventType, eventData, playerData) {
    const progress = loadQuestProgress();
    const allQuests = getAllQuestDefinitions();
    const completedQuests = [];

    allQuests.forEach(quest => {
      // Skip if wrong event type
      if (quest.eventType !== eventType) return;

      // Skip if already completed and claimed
      const questProgress = progress[quest.id] || {
        currentProgress: 0,
        completed: false,
        claimedReward: false
      };

      if (questProgress.claimedReward) return;

      // Update progress based on event
      let increment = 1;
      if (eventType === 'purchase_value') {
        increment = eventData.totalPrice || 0;
      }

      questProgress.currentProgress += increment;

      // Check for completion
      if (!questProgress.completed && questProgress.currentProgress >= quest.maxProgress) {
        questProgress.completed = true;
        completedQuests.push(quest);
        
        if (window.cyberSellsConfig?.debug) {
          console.log('[CyberSells] Quest completed:', quest.title);
        }
      }

      progress[quest.id] = questProgress;
    });

    // Save progress
    saveQuestProgress(progress);

    // Dispatch event if any quests completed
    if (completedQuests.length > 0) {
      document.dispatchEvent(new CustomEvent('cybersells:quest-update', {
        detail: { completedQuests }
      }));
    }

    return completedQuests;
  }

  /**
   * Claim quest reward
   */
  function claimQuestReward(questId, playerData) {
    const progress = loadQuestProgress();
    const quest = getAllQuestDefinitions().find(q => q.id === questId);

    if (!quest) {
      if (window.cyberSellsConfig?.debug) {
        console.warn('[CyberSells] Quest not found:', questId);
      }
      return null;
    }

    const questProgress = progress[questId];
    if (!questProgress || !questProgress.completed || questProgress.claimedReward) {
      if (window.cyberSellsConfig?.debug) {
        console.warn('[CyberSells] Quest cannot be claimed:', questId);
      }
      return null;
    }

    // Mark as claimed
    questProgress.claimedReward = true;
    progress[questId] = questProgress;
    saveQuestProgress(progress);

    if (window.cyberSellsConfig?.debug) {
      console.log('[CyberSells] Quest reward claimed:', quest.title);
    }

    return {
      quest,
      rewards: quest.reward
    };
  }

  /**
   * Track product view for quests
   */
  function trackProductView(productId) {
    if (!window.CyberSells_playerData) return;
    
    const playerData = window.CyberSells_playerData.loadPlayerData();
    
    // Track unique product views
    if (!playerData.productsViewed) {
      playerData.productsViewed = [];
    }
    
    if (!playerData.productsViewed.includes(productId)) {
      playerData.productsViewed.push(productId);
      window.CyberSells_playerData.savePlayerData(playerData);
      
      // Track quest progress
      trackQuestEvent('product_view', { productId }, playerData);
    }
  }

  /**
   * Track cart addition for quests
   */
  function trackCartAdd() {
    if (!window.CyberSells_playerData) return;
    
    const playerData = window.CyberSells_playerData.loadPlayerData();
    trackQuestEvent('cart_add', {}, playerData);
  }

  // Expose module functions
  window.CyberSells_quests = {
    getActiveQuests,
    trackQuestEvent,
    claimQuestReward,
    trackProductView,
    trackCartAdd,
    getAllQuestDefinitions
  };

  // Auto-track page views for quest progress
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initAutoTracking);
  } else {
    initAutoTracking();
  }

  function initAutoTracking() {
    // Track product page views
    const productMeta = document.querySelector('meta[property="og:type"][content="product"]');
    if (productMeta) {
      const productId = document.querySelector('[data-product-id]')?.dataset.productId;
      if (productId) {
        trackProductView(productId);
      }
    }

    // Track cart additions
    document.addEventListener('cart:add', () => {
      trackCartAdd();
    });
  }

})();
